import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { HttpService } from '../http/http.service';

export interface OnboardingProgress {
  user_id: string;
  current_step: number;
  step_1_completed: boolean;
  step_2_completed: boolean;
  step_3_completed: boolean;
  step_4_completed: boolean;
  step_5_completed: boolean;
  workflows?: string[];
  framework: string | null;
  is_complete: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private readonly _basePath = 'rest/v1/onboarding_progress';
  private _cachedComplete: boolean | null = null;

  constructor(
    private readonly _auth: AuthenticationService,
    private readonly _http: HttpService
  ) {}

  async loadProgress(): Promise<OnboardingProgress | null> {
    const userId = this._getUserId();
    if (!userId) {
      return null;
    }

    try {
      const url = `${this._basePath}?select=*&user_id=eq.${userId}&limit=1`;
      const response = await firstValueFrom(this._http.get(url, true));

      if (!response) {
        return null;
      }

      const record = Array.isArray(response) ? response[0] : response;
      if (!record) {
        return null;
      }

      return record as OnboardingProgress;
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      return null;
    }
  }

  async saveStepCompletion(
    step: number,
    data?: { workflows?: string[]; framework?: string }
  ): Promise<void> {
    const userId = this._getUserId();
    if (!userId) {
      throw new Error('No active session');
    }

    const payload: Record<string, unknown> = {
      user_id: userId,
      current_step: step + 1,
      [`step_${step}_completed`]: true,
      updated_at: new Date().toISOString(),
    };

    if (data?.workflows !== undefined) {
      payload['workflows'] = data.workflows;
    }
    if (data?.framework !== undefined) {
      payload['framework'] = data.framework;
    }

    const url = `${this._basePath}?on_conflict=user_id`;

    await firstValueFrom(
      this._http.post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
    );
  }

  async completeOnboarding(): Promise<void> {
    const userId = this._getUserId();
    if (!userId) {
      throw new Error('No active session');
    }

    const payload: Record<string, unknown> = {
      user_id: userId,
      step_5_completed: true,
      is_complete: true,
      current_step: 6,
      updated_at: new Date().toISOString(),
    };

    const url = `${this._basePath}?on_conflict=user_id`;

    await firstValueFrom(
      this._http.post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
    );

    this._cachedComplete = true;
  }

  async isOnboardingComplete(): Promise<boolean> {
    if (this._cachedComplete === true) {
      return true;
    }

    const progress = await this.loadProgress();
    if (!progress) {
      // No row = new user, onboarding not started yet
      // Edge function creates the row on signup, so no row means
      // either pre-existing user or row not created yet
      this._cachedComplete = null;
      return true; // Allow through — existing users without row skip onboarding
    }

    this._cachedComplete = progress.is_complete;
    return progress.is_complete;
  }

  clearCache(): void {
    this._cachedComplete = null;
  }

  private _getUserId(): string | null {
    const userDetail = this._auth.getUserDetail();
    return userDetail?.user?.id || (userDetail as any)?.id || null;
  }
}
